import React, { useEffect, useState } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/common/PageContainer';
import SectionHeader from '../components/common/SectionHeader';
import DataTable from '../components/common/DataTable';
import StatusChip from '../components/common/StatusChip';

const VARIANT_CONFIG = {
  retail: {
    orderType: 'retail',
    basePath: '/orders/public',
    sectionName: 'Pedidos al público',
    title: 'Pedidos al público',
    subtitle: 'Pedidos realizados para venta al público y su estado actual.',
  },
  wholesale: {
    orderType: 'wholesale',
    basePath: '/orders/business',
    sectionName: 'Pedidos mayoristas',
    title: 'Pedidos mayoristas',
    subtitle: 'Pedidos realizados por comercios y su estado actual.',
  },
};

const STATUS_FILTERS = [
  { value: '', label: 'Todos' },
  { value: 'borrador', label: 'Borrador' },
  { value: 'confirmado', label: 'Confirmado' },
  { value: 'en_elaboracion', label: 'En elaboración' },
  { value: 'listo', label: 'Listo p/ facturar' },
  { value: 'facturado', label: 'Facturado' },
  { value: 'entregado', label: 'Entregado' },
  { value: 'devuelto', label: 'Devuelto' },
  { value: 'cancelado', label: 'Cancelado' },
];

// Hasta que arranca la producción el pedido se puede editar (ítems, fecha, etc.).
// Después se abre su detalle: cambiar ítems en elaboración rompería el pesaje.
const EDITABLE_STATUSES = ['borrador', 'confirmado'];

const money = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }).format(Number(v) || 0);

const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// delivery_date llega como 'YYYY-MM-DD'; se arma en hora local para no correr el día
const formatDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-AR');
};

const OrderList = ({ variant = 'retail' }) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.retail;
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const isPortalUser = !!user?.is_wholesale_client;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setPage(0);
    setSearchTerm('');
    setStatusFilter('');
  }, [variant]);

  useEffect(() => {
    let cancelled = false;
    const delay = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/orders/', {
          params: {
            order_type: config.orderType,
            page: page + 1,
            page_size: rowsPerPage,
            status: statusFilter || undefined,
            search: searchTerm || undefined,
          },
        });
        if (cancelled) return;
        const data = response.data;
        setOrders(data.results || data || []);
        setTotalCount(data.count ?? (data.length || 0));
      } catch (error) {
        console.error('Error fetching orders:', error);
        if (!cancelled) {
          setOrders([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(delay);
    };
  }, [config.orderType, page, rowsPerPage, statusFilter, searchTerm]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Los mayoristas del portal no acceden a la ficha de producción: solo editan lo editable.
  const getOrderPath = (order) => {
    if (EDITABLE_STATUSES.includes(order.status)) return `/orders/${order.id}/edit`;
    return isPortalUser ? null : `/production/${order.id}`;
  };

  const columns = [
    {
      id: 'order_number',
      label: 'Pedido',
      render: (o) => (
        <Typography sx={{ fontWeight: 700, color: getOrderPath(o) ? '#800020' : '#0e141b' }}>
          #{o.order_number}
        </Typography>
      ),
    },
    ...(isPortalUser ? [] : [{
      id: 'client',
      label: 'Cliente',
      render: (o) => o.client?.name || <Typography component="span" sx={{ color: '#4e7397' }}>Consumidor final</Typography>,
    }]),
    { id: 'created_at', label: 'Creado', render: (o) => formatDateTime(o.created_at) },
    { id: 'delivery_date', label: 'Entrega', render: (o) => formatDate(o.delivery_date) },
    { id: 'item_count', label: 'Ítems', align: 'right', render: (o) => o.item_count ?? o.items?.length ?? 0 },
    { id: 'total', label: 'Total', align: 'right', render: (o) => <b>{money(o.total)}</b> },
    { id: 'status', label: 'Estado', render: (o) => <StatusChip status={o.status} /> },
  ];

  return (
    <PageContainer onLogout={handleLogout} maxWidth="xl">
      <SectionHeader
        title={isPortalUser ? 'Mis pedidos' : config.title}
        subtitle={isPortalUser ? 'Pedidos que realizaste y su estado actual.' : config.subtitle}
        breadcrumbs={[
          { label: config.sectionName, path: config.basePath },
          { label: 'Pedidos realizados' },
        ]}
        actions={[
          { label: 'Nuevo pedido', icon: <AddIcon />, onClick: () => navigate(config.basePath) },
        ]}
      />

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.value;
          return (
            <Chip
              key={f.value || 'all'}
              label={f.label}
              onClick={() => { setStatusFilter(f.value); setPage(0); }}
              variant={active ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                bgcolor: active ? '#800020' : '#ffffff',
                color: active ? '#ffffff' : '#4e7397',
                borderColor: '#d0dbe7',
                '&:hover': { bgcolor: active ? '#600010' : 'rgba(128,0,32,0.04)' },
              }}
            />
          );
        })}
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
          <span style={{ fontWeight: 700, color: '#0e141b' }}>{totalCount}</span> pedido(s)
        </Typography>
      </Box>

      <DataTable
        columns={columns}
        rows={orders}
        loading={loading}
        searchTerm={searchTerm}
        onSearchChange={(v) => { setSearchTerm(v); setPage(0); }}
        searchPlaceholder={isPortalUser ? 'Buscar por número de pedido...' : 'Buscar por número de pedido o cliente...'}
        noDataMessage="No hay pedidos para mostrar"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        onRowClick={(o) => navigate(getOrderPath(o))}
        isRowClickable={(o) => !!getOrderPath(o)}
        actions={(o) => {
          const path = getOrderPath(o);
          if (!path) return null;
          const editable = EDITABLE_STATUSES.includes(o.status);
          return (
            <Tooltip title={editable ? 'Editar pedido' : 'Ver detalle'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); navigate(path); }}
                sx={{ color: '#800020' }}
              >
                {editable ? <EditIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          );
        }}
      />
    </PageContainer>
  );
};

export default OrderList;
